import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { FiCamera, FiX, FiRefreshCw } from 'react-icons/fi';

const S = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  previewWrapper: {
    width: '100%',
    maxWidth: '360px',
    position: 'relative',
  },
  preview: {
    width: '100%',
    borderRadius: '16px',
    overflow: 'hidden',
    background: '#000',
    aspectRatio: '1',
  },
  scanningFrame: {
    position: 'absolute',
    inset: 0,
    border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '16px',
    pointerEvents: 'none',
  },
  corner: (pos) => ({
    position: 'absolute',
    width: '24px',
    height: '24px',
    borderColor: '#16a34a',
    borderStyle: 'solid',
    top: pos.includes('t') ? '12px' : 'auto',
    bottom: pos.includes('b') ? '12px' : 'auto',
    left: pos.includes('l') ? '12px' : 'auto',
    right: pos.includes('r') ? '12px' : 'auto',
    borderTopWidth: pos.includes('t') ? '3px' : 0,
    borderBottomWidth: pos.includes('b') ? '3px' : 0,
    borderLeftWidth: pos.includes('l') ? '3px' : 0,
    borderRightWidth: pos.includes('r') ? '3px' : 0,
    borderRadius:
      pos === 'tl'
        ? '6px 0 0 0'
        : pos === 'tr'
          ? '0 6px 0 0'
          : pos === 'bl'
            ? '0 0 0 6px'
            : '0 0 6px 0',
  }),
  scanLine: {
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #16a34a, transparent)',
    animation: 'scanline 2s ease-in-out infinite',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    borderRadius: '12px',
    border: 'none',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  btnCancel: {
    background: '#f1f5f9',
    color: '#475569',
  },
  btnRetry: {
    background: '#e8f0fe',
    color: '#1a73e8',
  },
  error: {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '12px',
    fontSize: '13px',
    textAlign: 'center',
    maxWidth: '360px',
  },
  loading: {
    color: '#6b7280',
    fontSize: '14px',
    textAlign: 'center',
    padding: '40px 20px',
  },
  scanningHint: {
    fontSize: '13px',
    color: '#16a34a',
    fontWeight: 500,
    textAlign: 'center',
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
};

export default function QRScanner({ onScan, onCancel, facingMode = 'environment' }) {
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const readerRef = useRef(null);

  const stopScanner = useCallback(async () => {
    const reader = readerRef.current;
    if (!reader) return;
    readerRef.current = null;
    try {
      await reader.stop();
    } catch (e) {
      /* ignorar */
    }
    try {
      reader.clear();
    } catch (e) {
      /* ignorar */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const startScanner = async () => {
      try {
        const reader = new Html5Qrcode('qr-scanner-preview');
        readerRef.current = reader;
        await reader.start(
          { facingMode },
          { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
          (decodedText) => {
            if (!cancelled) {
              setScanning(false);
              const reader = readerRef.current;
              readerRef.current = null;
              reader
                .stop()
                .then(() => {
                  onScan(decodedText);
                })
                .catch(() => {
                  onScan(decodedText);
                });
            }
          },
          () => {}
        );
        if (!cancelled) setScanning(true);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'No se pudo acceder a la camara');
        }
      }
    };

    const timer = setTimeout(startScanner, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facingMode]);

  const handleCancel = async () => {
    await stopScanner();
    setScanning(false);
    onCancel();
  };

  const handleRetry = async () => {
    setError(null);
    if (readerRef.current) {
      await stopScanner();
    }
    const startScanner = async () => {
      try {
        const reader = new Html5Qrcode('qr-scanner-preview');
        readerRef.current = reader;
        await reader.start(
          { facingMode },
          { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
          (decodedText) => {
            setScanning(false);
            const reader = readerRef.current;
            readerRef.current = null;
            reader
              .stop()
              .then(() => {
                onScan(decodedText);
              })
              .catch(() => {
                onScan(decodedText);
              });
          },
          () => {}
        );
        setScanning(true);
      } catch (err) {
        setError(err?.message || 'No se pudo acceder a la camara');
      }
    };
    startScanner();
  };

  return (
    <div style={S.container}>
      <div style={S.previewWrapper}>
        <div style={S.preview}>
          <div id="qr-scanner-preview" style={{ width: '100%', height: '100%' }} />
        </div>
        {scanning && !error && (
          <div style={S.scanningFrame}>
            <div style={S.corner('tl')} />
            <div style={S.corner('tr')} />
            <div style={S.corner('bl')} />
            <div style={S.corner('br')} />
          </div>
        )}
      </div>

      {!scanning && !error && (
        <div style={S.loading}>
          <FiCamera size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
          <div>Iniciando camara...</div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
            Permite el acceso cuando el navegador lo solicite
          </div>
        </div>
      )}

      {scanning && !error && (
        <div style={S.scanningHint}>
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#16a34a',
              animation: 'pulse 1.5s ease-in-out infinite',
              display: 'inline-block',
            }}
          />
          Escaneando...
        </div>
      )}

      {error && (
        <>
          <div style={S.error}>{error}</div>
          <div style={S.actions}>
            <button onClick={handleCancel} style={{ ...S.btn, ...S.btnCancel }}>
              <FiX size={16} /> Cancelar
            </button>
            <button onClick={handleRetry} style={{ ...S.btn, ...S.btnRetry }}>
              <FiRefreshCw size={16} /> Reintentar
            </button>
          </div>
        </>
      )}

      {scanning && !error && (
        <div style={S.actions}>
          <button onClick={handleCancel} style={{ ...S.btn, ...S.btnCancel }}>
            <FiX size={16} /> Cancelar
          </button>
        </div>
      )}
    </div>
  );
}
